import {
  useState,
  useRef,
  useEffect,
  useCallback,
  createContext,
  useContext,
  type FC,
} from "react";
import {
  ActionBarPrimitive,
  AuiIf,
  ErrorPrimitive,
  MessagePartPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
} from "@assistant-ui/react";
import { AlertCircle, Bug, CheckIcon, CopyIcon, DownloadIcon } from "lucide-react";
import { ArtifactDisplay } from "~/components/assistant-v2/ArtifactDisplay";
import { ChainOfThought } from "~/components/assistant-v2/ChainOfThought";
import { Composer, type ComposerProps } from "~/components/assistant-v2/Composer";
import { MarkdownText } from "~/components/assistant-v2/MarkdownText";
import { MessageFeedbackInput } from "~/components/assistant-v2/MessageFeedback";
import { TooltipIconButton } from "~/components/assistant-v2/TooltipIconButton";
import type { ContextBuffer } from "~/components/assistant-v2/useContextBuffer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { ZENDESK_AI_ASSISTANT_HELP_URL } from "~/components/assistant/constants";
import { CancellationMessage } from "~/components/assistant/messages/CancellationMessage";
import type {
  AssistantMessageCustomMetadata,
  MessageFeedbackCreate,
} from "~/components/assistant/types";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { TextOverflow } from "~/components/ui/text-overflow";
import { cn } from "~/lib/utils";

/**
 * Context providing a Map<messageId, turnId> for all streaming turns in
 * the current session. Historical turns get their turn IDs from message
 * metadata; this map covers messages created during streaming.
 */
const StreamingTurnIdsContext = createContext<Map<string, string>>(new Map());

/** Map of assistant-ui messageId → artifact names for streaming turns. */
const StreamingArtifactMapContext = createContext<Map<string, string[]>>(new Map());

/** Extract typed custom metadata from an assistant-ui message. */
function getCustomMetadata(message: {
  metadata?: unknown;
}): AssistantMessageCustomMetadata | undefined {
  return (message.metadata as { custom?: AssistantMessageCustomMetadata } | undefined)?.custom;
}

export interface ThreadProps extends ComposerProps {
  suggestions?: Array<{ title: string; prompt: string }>;
  contextBuffer?: ContextBuffer | null;
  isLoadingHistory?: boolean;
  /** True after history loading completes and at least one message was found. */
  historyHasMessages?: boolean;
  runError?: string | null;
  /** Map of messageId → turnId for all streaming turns in this session. */
  streamingTurnIds?: Map<string, string>;
  /** Map of assistant-ui messageId → artifact names for streaming turns. */
  streamingArtifactMap?: Map<string, string[]>;
}

// @lat: [[frontend/ai-assistant#V2 AG-UI Assistant]]
export const Thread: FC<ThreadProps> = ({
  suggestions,
  contextBuffer,
  isLoadingHistory,
  historyHasMessages,
  runError,
  streamingTurnIds,
  streamingArtifactMap,
  isRunningRemotely,
  ...composerProps
}) => {
  return (
    <StreamingTurnIdsContext.Provider value={streamingTurnIds ?? new Map()}>
      <StreamingArtifactMapContext.Provider value={streamingArtifactMap ?? new Map()}>
        <ThreadPrimitive.Root
          className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
          style={{
            ["--thread-max-width" as string]: "56rem",
          }}
        >
          <ThreadPrimitive.Viewport
            turnAnchor="top"
            autoScroll
            className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-auto scroll-smooth px-4 pt-4 scrollbar-subtle"
          >
            <ThreadEmptyState
              isLoadingHistory={isLoadingHistory}
              historyHasMessages={historyHasMessages}
              suggestions={suggestions}
            />

            <ThreadPrimitive.Messages
              components={{
                UserMessage,
                AssistantMessage,
              }}
            />

            {isRunningRemotely && (
              <div className="mx-auto w-full max-w-(--thread-max-width) py-4 px-2">
                <ThinkingIndicator />
              </div>
            )}

            <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-(--thread-max-width) flex-col gap-2 overflow-visible rounded-t-3xl bg-background">
              {runError && <RunError message={runError} />}
              <div className="relative mx-auto w-full max-w-(--thread-max-width)">
                <div className="relative z-20">
                  <Composer
                    {...composerProps}
                    isRunningRemotely={isRunningRemotely}
                    contextBuffer={contextBuffer}
                  />
                </div>
                <CancellingBanner isVisible={composerProps.isCancelling === true} />
              </div>
              <div className="text-center text-xs text-gray-500 pb-2">
                AI can make mistakes. Review for accuracy.
              </div>
            </ThreadPrimitive.ViewportFooter>
          </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
      </StreamingArtifactMapContext.Provider>
    </StreamingTurnIdsContext.Provider>
  );
};

const MessageSkeletons: FC = () => {
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width) flex flex-col gap-6 py-4 animate-pulse">
      {[0, 1, 2].map((turn) => (
        <div key={turn} className="flex flex-col gap-4">
          <div className="flex justify-end px-2">
            <div className="h-10 w-2/5 rounded-2xl bg-muted" />
          </div>
          <div className="flex flex-col gap-2 px-2">
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-4 w-1/2 rounded bg-muted" />
            <div className="h-4 w-5/6 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
};

const SuggestionButton: FC<{
  ariaLabel: string;
  title: string;
  onClick?: () => void;
}> = ({ ariaLabel, title, onClick }) => (
  <Button
    variant="ghost"
    onClick={onClick}
    className="aui-thread-welcome-suggestion h-auto w-full items-start justify-start overflow-hidden rounded-xl border px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
    aria-label={ariaLabel}
  >
    <TextOverflow
      autoTooltip
      delayDuration={700}
      className="aui-thread-welcome-suggestion-text-1 block w-full font-medium"
    >
      {title}
    </TextOverflow>
  </Button>
);

/**
 * Shows loading skeletons while history is being fetched, or the welcome
 * screen when the thread is genuinely empty.
 *
 * Uses `historyHasMessages` (a deterministic React-state signal from the
 * persistence hook) instead of `thread.isEmpty` (an external store value
 * that settles asynchronously after `runtime.thread.reset()`). This
 * eliminates the one-frame flicker where the welcome screen appears
 * between "loading done" and "messages populated in the external store".
 */
const ThreadEmptyState: FC<{
  isLoadingHistory?: boolean;
  historyHasMessages?: boolean;
  suggestions?: Array<{ title: string; prompt: string }>;
}> = ({ isLoadingHistory, historyHasMessages, suggestions }) => {
  if (isLoadingHistory) {
    return <MessageSkeletons />;
  }

  if (historyHasMessages) {
    return null;
  }

  return (
    <AuiIf condition={({ thread }) => thread.isEmpty}>
      <ThreadWelcome suggestions={suggestions} />
    </AuiIf>
  );
};

interface ThreadWelcomeProps {
  suggestions?: Array<{ title: string; prompt: string }>;
}

const ThreadWelcome: FC<ThreadWelcomeProps> = ({ suggestions }) => {
  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="flex w-full flex-1 flex-col justify-center px-4">
        <Badge
          className={cn(
            "mb-3 w-fit rounded-[6px] text-primary",
            "px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
          )}
        >
          Beta
        </Badge>
        <h1 className="text-xl font-semibold leading-tight">Analysis Agent</h1>
        <p className="my-2 text-sm leading-relaxed text-gray-600">
          Ask questions, generate tables or charts, and compare data across tables.{" "}
          <a
            href={ZENDESK_AI_ASSISTANT_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline cursor-pointer"
          >
            View Usage Guide
          </a>
        </p>
      </div>
      <ThreadSuggestions suggestions={suggestions} />
    </div>
  );
};

const ThreadSuggestions: FC<{
  suggestions?: Array<{ title: string; prompt: string }>;
}> = ({ suggestions }) => {
  return (
    <div className="aui-thread-welcome-suggestions grid w-full gap-2 pb-4 @md:grid-cols-2">
      {suggestions?.map((suggestion, index) => (
        <div
          key={suggestion.prompt}
          className="aui-thread-welcome-suggestion-display min-w-0 fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200"
          style={{ animationDelay: `${100 + index * 50}ms` }}
        >
          <ThreadPrimitive.Suggestion prompt={suggestion.prompt} asChild>
            <SuggestionButton ariaLabel={suggestion.prompt} title={suggestion.title} />
          </ThreadPrimitive.Suggestion>
        </div>
      ))}
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const CancellingBanner: FC<{ isVisible: boolean }> = ({ isVisible }) => {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-4 bottom-[calc(100%)] z-10",
        "transform-gpu transition-all duration-200 ease-out",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      )}
      aria-hidden={!isVisible}
    >
      <div className="mx-auto flex w-fit items-center justify-center gap-2 rounded-t-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 shadow-sm">
        <div className="size-3 animate-spin rounded-full border-1 border-current border-t-transparent" />
        Cancelling...
      </div>
    </div>
  );
};

const RunError: FC<{ message: string }> = ({ message }) => {
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width) px-2 py-3">
      <div className="flex items-start gap-3 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <AlertCircle className="mt-0.5 size-4 shrink-0" />
        <span>{message}</span>
      </div>
    </div>
  );
};

/**
 * Animated ping dot with optional shimmer label.
 *
 * - No text: renders just the dot (use inline after streaming text).
 * - With text: renders dot + shimmer label (use as a block indicator).
 */
const ThinkingIndicator: FC<{
  text?: string;
  className?: string;
  showPulse?: boolean;
}> = ({ text = "Thinking", className, showPulse = false }) => (
  <span className={cn("inline-flex items-center gap-1.5 text-sm text-gray-500", className)}>
    {showPulse && (
      <span className="relative inline-flex size-2 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-gray-400 opacity-75" />
        <span className="relative inline-flex size-2 rounded-full bg-gray-400" />
      </span>
    )}
    {text && <ShimmerText className="text-sm font-medium">{text}</ShimmerText>}
  </span>
);

/**
 * Renders text parts in assistant messages as markdown.
 *
 * The backend emits LLM reasoning narration as ReasoningMessage events
 * (separate part type handled by assistant-ui), so any regular text part
 * here is the final response content.
 */
const AssistantText: FC = () => {
  const part = useAuiState((s) => s.part);
  const textContent = (part as { text: string }).text;
  if (!textContent) return null;

  return (
    <>
      <MarkdownText />
      <MessagePartPrimitive.InProgress>
        <ThinkingIndicator className="ml-1" />
      </MessagePartPrimitive.InProgress>
    </>
  );
};

/**
 * Shows a "Thinking" indicator when the message is running but no content
 * parts have arrived yet (e.g. right after submitting a prompt, or the
 * brief gap between agent cycles). Once any part appears — reasoning,
 * tool-call, or text — the CoT and Text components take over.
 */
const MessageRunningEmpty: FC = () => {
  const isRunning = useAuiState((s) => s.message.status?.type === "running");
  const hasContent = useAuiState((s) => (s.message.content?.length ?? 0) > 0);

  if (!isRunning || hasContent) return null;
  return <ThinkingIndicator />;
};

/**
 * Renders artifacts for the current assistant message.
 *
 * For history messages, artifact names are stored in metadata.custom.
 * For streaming messages, they are looked up from the per-message
 * StreamingArtifactMapContext using the assistant-ui message ID.
 */
const MessageArtifacts: FC = () => {
  const message = useAuiState((s) => s.message);
  const streamingArtifactMap = useContext(StreamingArtifactMapContext);

  // History: artifact names from metadata
  const custom = getCustomMetadata(message);
  if (custom?.artifactNames && custom.artifactNames.length > 0) {
    return <ArtifactDisplay artifactNames={custom.artifactNames} />;
  }

  // Streaming: look up artifacts for this specific message by its ID.
  const streamingNames = streamingArtifactMap.get(message.id);
  if (streamingNames && streamingNames.length > 0) {
    return <ArtifactDisplay artifactNames={streamingNames} />;
  }

  return null;
};

const AssistantMessage: FC = () => {
  const message = useAuiState((s) => s.message);
  const showCancelled =
    getCustomMetadata(message)?.cancelled === true ||
    (message.status?.type === "incomplete" && message.status?.reason === "cancelled");

  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
    >
      <div className="aui-assistant-message-content wrap-break-word px-2 text-foreground leading-relaxed text-[14px]">
        {showCancelled ? (
          <CancellationMessage />
        ) : (
          <>
            <MessagePrimitive.Parts
              components={{
                Text: AssistantText,
                ChainOfThought,
              }}
            />

            <MessageRunningEmpty />
            <MessageArtifacts />
            <MessageError />
          </>
        )}
      </div>

      <div className="aui-assistant-message-footer mt-1 ml-2 flex">
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

/**
 * Resolves the turn ID and existing feedback for the turn that this
 * assistant message belongs to.
 *
 * For history-loaded messages, the turn ID lives in metadata.custom.
 * For streamed messages, it's looked up from the streaming turn IDs map
 * (populated when RunStartedEvent fires during the SSE stream).
 */
function useTurnFeedbackInfo() {
  const streamingTurnIds = useContext(StreamingTurnIdsContext);
  const message = useAuiState((s) => s.message);

  // History-loaded messages carry the turn ID in metadata.custom
  const custom = getCustomMetadata(message);
  if (custom?.turnId) {
    return { turnId: custom.turnId, feedback: custom.feedback ?? null };
  }

  // Streamed messages: look up by message ID
  const turnId = streamingTurnIds.get(message.id);
  if (turnId) {
    return { turnId, feedback: null };
  }

  return null;
}

const AssistantActionBar: FC = () => {
  const { api, conversationId } = useAssistantContext();
  const message = useAuiState((s) => s.message);
  const info = useTurnFeedbackInfo();

  // Hide the action bar while this specific message is still being generated
  const isRunning = message.status?.type === "running";

  const [feedback, setFeedback] = useState<MessageFeedbackCreate | null>(info?.feedback ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync with history-loaded feedback when info changes (e.g. conversation switch)
  useEffect(() => {
    setFeedback(info?.feedback ?? null);
  }, [info?.feedback]);

  const handleSubmit = useCallback(
    async (value: 0 | 1, text?: string | null) => {
      if (!conversationId || !info?.turnId || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const submitted = {
          feedback_type: "BINARY" as const,
          feedback_value: value,
          text: text ?? null,
        };
        await api.putTurnFeedback(conversationId, info.turnId, submitted);
        setFeedback(submitted);
      } catch (error) {
        console.error("Failed to submit feedback:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [api, conversationId, info?.turnId, isSubmitting]
  );

  const handleClear = useCallback(async () => {
    if (!conversationId || !info?.turnId || isSubmitting || !api.deleteTurnFeedback) return;
    setIsSubmitting(true);
    try {
      await api.deleteTurnFeedback(conversationId, info.turnId);
      setFeedback(null);
    } catch (error) {
      console.error("Failed to clear feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [api, conversationId, info?.turnId, isSubmitting]);

  if (isRunning) return null;

  return (
    <ActionBarPrimitive.Root
      autohide="never"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy">
          <AuiIf condition={({ message }) => message.isCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={({ message }) => !message.isCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.ExportMarkdown asChild>
        <TooltipIconButton tooltip="Export as Markdown">
          <DownloadIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.ExportMarkdown>
      {info?.turnId && (
        <MessageFeedbackInput
          feedback={feedback}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onClear={api.deleteTurnFeedback ? handleClear : undefined}
        />
      )}
    </ActionBarPrimitive.Root>
  );
};

function TraceId({ traceId, debugMode }: { traceId?: string; debugMode: boolean }) {
  if (!debugMode || !traceId) {
    return null;
  }

  return (
    <div className="sticky top-0 self-start" onClick={(e) => e.stopPropagation()}>
      <CopyButton
        text={traceId}
        icon={Bug}
        tooltipText={`Copy Trace ID: ${traceId}`}
        variant="light"
        size="sm"
      />
    </div>
  );
}

/** ~3 lines at 14px font / relaxed leading */
const USER_MSG_COLLAPSED_HEIGHT = 70;

const UserMessage: FC = () => {
  const { debugMode = false } = useAssistantContext();
  const message = useAuiState((s) => s.message);
  const traceId = (message.metadata as Record<string, unknown> | undefined)?.traceId as
    | string
    | undefined;

  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const check = () => setIsOverflowing(el.scrollHeight > USER_MSG_COLLAPSED_HEIGHT);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const collapse = useCallback(() => {
    setIsExpanded(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    function handleClick(e: MouseEvent) {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        collapse();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isExpanded, collapse]);

  const expand = useCallback(() => {
    if (isOverflowing && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isOverflowing, isExpanded]);

  return (
    <MessagePrimitive.Root
      className="aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div
          ref={contentRef}
          role={isOverflowing ? "button" : undefined}
          tabIndex={isOverflowing ? 0 : undefined}
          onClick={expand}
          onKeyDown={
            isOverflowing && !isExpanded ? (e) => e.key === "Enter" && expand() : undefined
          }
          className={cn(
            "aui-user-message-content wrap-break-word rounded-2xl bg-muted px-4 py-2.5 text-foreground text-[14px] relative grid w-full items-start",
            isOverflowing && !isExpanded && "cursor-pointer",
            debugMode && traceId ? "grid-cols-[1fr_30px] gap-1" : "grid-cols-[1fr]"
          )}
          style={{
            maxHeight: isExpanded ? "20vh" : `${USER_MSG_COLLAPSED_HEIGHT}px`,
            overflowY: isExpanded ? "auto" : "hidden",
          }}
        >
          <MessagePrimitive.Parts />
          <TraceId traceId={traceId} debugMode={debugMode} />
          {isOverflowing && !isExpanded && (
            <div
              className="absolute bottom-0 left-0 right-0 h-8 rounded-b-2xl pointer-events-none bg-muted"
              style={{
                maskImage: "linear-gradient(to bottom, transparent, black)",
                WebkitMaskImage: "linear-gradient(to bottom, transparent, black)",
              }}
            />
          )}
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};
